import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useCombobox } from 'downshift';
import { useFormState } from './formContext';
import { PickerFeedback, PickerOptionContent, pickerKeyDown, usePickerAnnouncement, usePickerValidity } from './pickerSupport';
import type { ComboboxProps, PickerOption } from './pickerTypes';

export type { ComboboxProps, PickerOption } from './pickerTypes';

export const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(function Combobox({
  id, name, required, label, value, query, options, loading, error, onQueryChange, onChange, onRetry,
  disabled = false, disabledReason, unavailable, placeholder, renderOption, className,
  'aria-required': ariaRequired, 'aria-invalid': ariaInvalid, 'aria-describedby': describedBy,
}, ref) {
  const input = useRef<HTMLInputElement>(null);
  const composing = useRef(false);
  const [editing, setEditing] = useState(false);
  const form = useFormState();
  const inactive = disabled || Boolean(unavailable) || Boolean(form?.busy);
  const reason = unavailable || disabledReason || (form?.busy ? 'Unavailable while saving.' : undefined);
  const items = useMemo(() => loading || error || inactive ? [] : [...options], [options, loading, error, inactive]);
  useImperativeHandle(ref, () => input.current!);
  usePickerValidity(input, required, Boolean(value));
  const {
    isOpen, highlightedIndex, getLabelProps, getInputProps, getMenuProps, getItemProps, getToggleButtonProps,
  } = useCombobox<PickerOption>({
    id: `${id}-picker`, inputId: id, labelId: `${id}-picker-label`, menuId: `${id}-menu`,
    getItemId: (index) => `${id}-option-${index}`,
    items, itemToString: (item) => item?.label ?? '', itemToKey: (item) => item?.id ?? '',
    isItemDisabled: (item) => Boolean(item.disabledReason),
    inputValue: editing && !inactive ? query : value?.label ?? query, selectedItem: value,
    onInputValueChange: ({ inputValue }) => onQueryChange(inputValue ?? ''),
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem && !inactive && !selectedItem.disabledReason) {
        onChange(selectedItem); form?.markChanged(); setEditing(false);
      }
    },
    stateReducer: (state, { type, changes }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          return { ...changes, selectedItem: state.selectedItem, inputValue: state.inputValue };
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return { ...changes, inputValue: '' };
        default: return changes;
      }
    },
  });
  const open = isOpen && !inactive;
  const announcement = usePickerAnnouncement(query, items.length, loading, error, unavailable, open);
  const inputProps = getInputProps({
    ref: input, name, required, disabled: inactive, placeholder,
    'aria-required': ariaRequired || required || undefined, 'aria-invalid': ariaInvalid,
    'aria-describedby': [describedBy, reason ? `${id}-availability` : ''].filter(Boolean).join(' ') || undefined,
    onChange: () => setEditing(true),
    onBlur: () => { setEditing(false); onQueryChange(''); },
  });
  return <div className={`ui-picker ${className ?? ''}`}>
    {/* Names the listbox only. The input is labelled by FormField's <label>; a second
        <label for> would make its accessible name "Label Label". */}
    <span id={getLabelProps().id} className="ui-picker__label">{label}</span>
    <div className="ui-picker__control">
      <input {...inputProps} className="ui-control" onKeyDown={(event) => {
        const isComposing = composing.current || event.nativeEvent.isComposing || event.keyCode === 229;
        pickerKeyDown(event, open, highlightedIndex, isComposing, inputProps.onKeyDown);
        if (!isComposing && event.key === 'Escape' && open) { setEditing(false); onQueryChange(''); }
        if (!isComposing && event.key === 'Enter' && open && items[highlightedIndex]
          && !items[highlightedIndex]!.disabledReason) setEditing(false);
      }} aria-expanded={open}
      aria-activedescendant={open && items[highlightedIndex] ? `${id}-option-${highlightedIndex}` : undefined}
      onCompositionStart={() => { composing.current = true; }} onCompositionEnd={() => { composing.current = false; }} />
      <button {...getToggleButtonProps({ disabled: inactive })} type="button" className="ui-button"
        aria-label={`Toggle ${label} options`}>▾</button>
    </div>
    <ul {...getMenuProps()} className="ui-picker__menu" hidden={!open || !items.length} aria-busy={loading || undefined}>
      {open && items.map((option, index) => {
        const itemProps = getItemProps({ item: option, index });
        return <li key={option.id} {...itemProps}
          aria-selected={value?.id === option.id} aria-disabled={Boolean(option.disabledReason)}
          aria-describedby={option.disabledReason ? `${id}-reason-${index}` : undefined}
          onClick={(event) => { itemProps.onClick?.(event); if (!option.disabledReason) setEditing(false); }}
          className={`ui-picker__option ${highlightedIndex === index ? 'ui-picker__option--active' : ''}`}>
          {renderOption ? renderOption(option) : <PickerOptionContent option={option} />}
          {option.disabledReason && <span id={`${id}-reason-${index}`} className="ui-picker__detail">{option.disabledReason}</span>}
        </li>;
      })}
    </ul>
    <PickerFeedback id={id} loading={loading} error={error} onRetry={onRetry} unavailable={unavailable}
      disabledReason={reason} disabled={inactive} empty={open && !items.length} />
    <span className="ui-picker__label" role="status" aria-live="polite" aria-atomic="true">{announcement}</span>
  </div>;
});
